/**
 * M4: Public supplier quote portal (CODA-1814)
 *
 * Token-secured page for suppliers — no login required.
 * Supplier enters prices, delivery time, availability and remarks.
 */
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { Button, Input, Select, Textarea, ErrorState } from '@/components/ui'

const AVAILABILITY_OPTIONS = [
  { value: 'sofort_verfuegbar', label: 'Sofort verfügbar' },
  { value: 'teilweise_verfuegbar', label: 'Teilweise verfügbar' },
  { value: 'nicht_verfuegbar', label: 'Nicht verfügbar' },
  { value: 'auf_anfrage', label: 'Auf Anfrage' },
]

// ─── Types ────────────────────────────────────────────────────────────────────

interface PortalItem {
  id: string
  itemName: string
  itemDescription: string | null
  quantity: string
  unit: string
  itemCode: string | null
}

interface ExistingQuote {
  rfqItemId: string
  unitPrice: string
  deliveryDays: number | null
  availability: string | null
  itemNote: string | null
}

interface PortalData {
  supplierName: string
  contactName: string | null
  rfqNumber: number
  deadlineDate: string
  notes: string | null
  isExpired: boolean
  alreadySubmitted: boolean
  items: PortalItem[]
  existingQuotes: ExistingQuote[]
}

interface QuoteEntry {
  rfqItemId: string
  unitPrice: string
  deliveryDays: string
  availability: string
  itemNote: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function buildInitialQuotes(items: PortalItem[], existing: ExistingQuote[]): QuoteEntry[] {
  return items.map((item) => {
    const eq = existing.find((q) => q.rfqItemId === item.id)
    return {
      rfqItemId: item.id,
      unitPrice: eq ? String(parseFloat(eq.unitPrice)) : '',
      deliveryDays: eq?.deliveryDays != null ? String(eq.deliveryDays) : '',
      availability: eq?.availability ?? '',
      itemNote: eq?.itemNote ?? '',
    }
  })
}

// ─── Countdown component ──────────────────────────────────────────────────────

function DeadlineCountdown({ deadlineDate }: { deadlineDate: string }) {
  const [remaining, setRemaining] = useState<{ days: number; hours: number; expired: boolean }>(() =>
    calcRemaining(deadlineDate),
  )

  useEffect(() => {
    const id = setInterval(() => setRemaining(calcRemaining(deadlineDate)), 30_000)
    return () => clearInterval(id)
  }, [deadlineDate])

  if (remaining.expired) {
    return (
      <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-4 py-1.5 text-sm font-medium text-red-700">
        <span>⏰</span> Frist abgelaufen
      </div>
    )
  }

  const urgencyColor =
    remaining.days <= 1
      ? 'bg-red-100 text-red-700'
      : remaining.days <= 3
        ? 'bg-amber-100 text-amber-700'
        : 'bg-emerald-100 text-emerald-700'

  return (
    <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium ${urgencyColor}`}>
      <span>⏱</span>
      {remaining.days > 0 ? (
        <>
          Noch {remaining.days} {remaining.days === 1 ? 'Tag' : 'Tage'} ({remaining.hours}{' '}
          {remaining.hours === 1 ? 'Stunde' : 'Stunden'})
        </>
      ) : (
        <>Noch {remaining.hours} {remaining.hours === 1 ? 'Stunde' : 'Stunden'}</>
      )}
    </div>
  )
}

function calcRemaining(dateStr: string) {
  const deadlineMs = new Date(dateStr + 'T23:59:59').getTime()
  const diff = deadlineMs - Date.now()
  if (diff <= 0) return { days: 0, hours: 0, expired: true }
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  return { days, hours, expired: false }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function SupplierPortalPage() {
  const { token } = useParams<{ token: string }>()
  const [quotes, setQuotes] = useState<QuoteEntry[]>([])
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, isError, error } = useQuery<PortalData>({
    queryKey: ['supplier-portal', token],
    queryFn: () =>
      axios.get(`/api/public/supplier-rfq/${token}`).then((r) => r.data as PortalData),
    enabled: !!token,
    retry: false,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (data) {
      setQuotes(buildInitialQuotes(data.items, data.existingQuotes))
      if (data.alreadySubmitted) setSubmitted(true)
    }
  }, [data])

  const submitMutation = useMutation({
    mutationFn: () => {
      const payload = quotes.map((q) => ({
        rfqItemId: q.rfqItemId,
        unitPrice: parseFloat(q.unitPrice) || 0,
        deliveryDays: q.deliveryDays ? parseInt(q.deliveryDays, 10) : null,
        availability: q.availability || null,
        itemNote: q.itemNote || null,
      }))
      return axios.post(`/api/public/supplier-rfq/${token}`, { quotes: payload })
    },
    onSuccess: () => setSubmitted(true),
  })

  function updateQuote(idx: number, field: keyof QuoteEntry, value: string) {
    setQuotes((prev) => prev.map((q, i) => (i === idx ? { ...q, [field]: value } : q)))
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <PublicShell>
        <div className="flex min-h-[300px] items-center justify-center text-slate-400">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            <p className="text-sm">Anfrage wird geladen …</p>
          </div>
        </div>
      </PublicShell>
    )
  }

  // ── Error ──
  if (isError || !data) {
    const errAny = error as { response?: { data?: { message?: string }; status?: number } } | null
    const errMsg =
      errAny?.response?.data?.message ??
      'Dieser Link ist nicht mehr gültig. Bitte wende dich an deinen Ansprechpartner.'
    return (
      <PublicShell>
        <ErrorState
          title="Link ungültig oder abgelaufen"
          description={errMsg}
          details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error), 2) : undefined}
        />
      </PublicShell>
    )
  }

  // ── Expired ──
  if (data.isExpired && !submitted) {
    return (
      <PublicShell rfqNumber={data.rfqNumber} supplierName={data.supplierName}>
        <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
          <div className="mb-3 text-4xl">🔒</div>
          <h2 className="mb-2 text-xl font-semibold text-red-800">Angebotsfrist abgelaufen</h2>
          <p className="text-red-600">
            Die Frist für Angebotsanfrage <strong>#{data.rfqNumber}</strong> ist am{' '}
            {formatDate(data.deadlineDate)} abgelaufen. Eine Einreichung ist nicht mehr möglich.
          </p>
          <p className="mt-3 text-sm text-red-500">
            Falls Sie Fragen haben, wenden Sie sich bitte direkt an uns.
          </p>
        </div>
      </PublicShell>
    )
  }

  // ── Success ──
  if (submitted) {
    return (
      <PublicShell rfqNumber={data.rfqNumber} supplierName={data.supplierName}>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center">
          <div className="mb-3 text-5xl">✅</div>
          <h2 className="mb-2 text-xl font-semibold text-emerald-800">
            Angebot erfolgreich übermittelt
          </h2>
          <p className="text-emerald-700">
            Vielen Dank! Ihr Angebot für Anfrage <strong>#{data.rfqNumber}</strong> wurde
            erfolgreich eingereicht.
          </p>
          {!data.isExpired && (
            <p className="mt-3 text-sm text-emerald-600">
              Sie können Ihr Angebot bis zum {formatDate(data.deadlineDate)} noch bearbeiten.
            </p>
          )}
          {!data.isExpired && (
            <Button
              variant="secondary"
              className="mt-5"
              onClick={() => setSubmitted(false)}
            >
              Angebot bearbeiten
            </Button>
          )}
        </div>
      </PublicShell>
    )
  }

  // ── Form ──
  const hasErrors = submitMutation.isError
  const submitErrMsg =
    hasErrors
      ? (submitMutation.error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Übermittlung fehlgeschlagen. Bitte versuche es erneut.'
      : null

  return (
    <PublicShell rfqNumber={data.rfqNumber} supplierName={data.supplierName}>
      {/* Meta info */}
      <div className="mb-6 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Anfrage-Nr.</p>
          <p className="font-semibold text-slate-800">#{data.rfqNumber}</p>
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Angebotsfrist</p>
          <div className="mt-0.5 flex items-center gap-2">
            <p className="font-semibold text-slate-800">{formatDate(data.deadlineDate)}</p>
            <DeadlineCountdown deadlineDate={data.deadlineDate} />
          </div>
        </div>
        {data.contactName && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Ansprechpartner</p>
            <p className="font-semibold text-slate-800">{data.contactName}</p>
          </div>
        )}
        {data.notes && (
          <div className="sm:col-span-2">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Hinweise</p>
            <p className="mt-0.5 text-sm text-slate-700">{data.notes}</p>
          </div>
        )}
      </div>

      {/* Items */}
      <div className="space-y-4">
        {data.items.map((item, idx) => {
          const q = quotes[idx]
          if (!q) return null
          return (
            <div
              key={item.id}
              className="rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              {/* Position header */}
              <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 rounded-t-xl">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{item.itemName}</p>
                    {item.itemCode && (
                      <p className="text-xs text-slate-400">Artikelnr.: {item.itemCode}</p>
                    )}
                    {item.itemDescription && (
                      <p className="mt-0.5 text-sm text-slate-500">{item.itemDescription}</p>
                    )}
                  </div>
                  <div className="shrink-0 rounded-full bg-brand-100 px-3 py-0.5 text-sm font-medium text-brand-700">
                    {parseFloat(item.quantity) % 1 === 0
                      ? parseInt(item.quantity).toLocaleString('de-DE')
                      : parseFloat(item.quantity).toLocaleString('de-DE')}{' '}
                    {item.unit}
                  </div>
                </div>
              </div>

              {/* Input fields */}
              <div className="grid gap-4 p-4 sm:grid-cols-2">
                <Input
                  label="Preis netto/Einheit (€)"
                  type="number"
                  min={0}
                  step="0.01"
                  value={q.unitPrice}
                  onChange={(e) => updateQuote(idx, 'unitPrice', e.target.value)}
                  placeholder="0,00"
                />
                <Input
                  label="Lieferzeit (Werktage)"
                  type="number"
                  min={0}
                  step={1}
                  value={q.deliveryDays}
                  onChange={(e) => updateQuote(idx, 'deliveryDays', e.target.value)}
                  placeholder="z.B. 5"
                />
                <Select
                  label="Verfügbarkeit"
                  value={q.availability || ''}
                  onValueChange={(v) => updateQuote(idx, 'availability', v)}
                  options={AVAILABILITY_OPTIONS}
                  placeholder="– bitte wählen –"
                />
                <Textarea
                  label="Bemerkung"
                  value={q.itemNote}
                  onChange={(e) => updateQuote(idx, 'itemNote', e.target.value)}
                  placeholder="Optionale Anmerkungen zu dieser Position"
                  rows={2}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Error */}
      {submitErrMsg && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {submitErrMsg}
        </div>
      )}

      {/* Submit */}
      <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-end">
        <p className="text-xs text-slate-400 sm:mr-auto">
          Sie können Ihr Angebot bis zum {formatDate(data.deadlineDate)} jederzeit aktualisieren.
        </p>
        <Button
          variant="primary"
          size="lg"
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? 'Wird übermittelt …' : 'Angebot absenden'}
        </Button>
      </div>
    </PublicShell>
  )
}

// ─── Shell ────────────────────────────────────────────────────────────────────

function PublicShell({
  children,
  rfqNumber,
  supplierName,
}: {
  children: React.ReactNode
  rfqNumber?: number
  supplierName?: string
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
              <span className="text-base font-bold text-white">S</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Implec GmbH</p>
              <p className="text-xs text-slate-400">Lieferanten-Angebotsportal</p>
            </div>
          </div>
          {rfqNumber && (
            <div className="text-right">
              <p className="text-xs text-slate-400">Angebotsanfrage</p>
              <p className="text-sm font-semibold text-slate-700">#{rfqNumber}</p>
            </div>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-3xl px-4 py-8">
        {supplierName && (
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900">
              Angebotsanfrage
            </h1>
            <p className="mt-1 text-slate-500">
              Sehr geehrte Damen und Herren von <strong>{supplierName}</strong>, bitte füllen Sie
              die folgenden Positionen aus und senden Sie Ihr Angebot ab.
            </p>
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-slate-200 py-6 text-center text-xs text-slate-400">
        Implec GmbH · Angebotsportal · Alle Preise netto zzgl. gesetzl. MwSt.
      </footer>
    </div>
  )
}
